import React, { useState, useEffect, useCallback } from 'react';
import { FiTag, FiEdit2, FiTrash2, FiPlus, FiX, FiCheck, FiSearch, FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import PageHero from './PageHero';
import PageHelp from './PageHelp';
import { Skeleton, SkeletonText } from './Skeleton';
import { showToast } from './NotificationTray';

export default function Tags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#304849');
  const [editingTag, setEditingTag] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [savingTag, setSavingTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [taggedImages, setTaggedImages] = useState({});
  const [loadingImages, setLoadingImages] = useState(false);
  const [viewMode, setViewMode] = useState('manage'); // 'manage', 'browse', or 'view'
  const [listMode, setListMode] = useState('cards'); // cards | table (manage only)
  const [browseFilterColor, setBrowseFilterColor] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null); // For image lightbox

  const colorPresets = [
    '#ef4444', // red
    '#f97316', // orange
    '#f59e0b', // amber
    '#eab308', // yellow
    '#84cc16', // lime
    '#22c55e', // green
    '#10b981', // emerald
    '#14b8a6', // teal
    '#06b6d4', // cyan
    '#0ea5e9', // sky
    '#3b82f6', // blue
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#a855f7', // purple
    '#d946ef', // fuchsia
    '#ec4899', // pink
    '#f43f5e', // rose
    '#4a5f5d', // slate
  ];

  useEffect(() => {
    loadTags();
  }, []);

  async function loadTags() {
    setLoading(true);
    try {
      const res = await fetch('/api/tags', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTags(data);
        // Load images for each tag
        loadTaggedImages(data);
      }
    } catch (error) {
      console.error('Failed to load tags:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadTaggedImages(tagsToLoad) {
    setLoadingImages(true);
    const imagesByTag = {};
    
    try {
      for (const tag of tagsToLoad) {
        const res = await fetch(`/api/images?tag_id=${tag.id}&limit=12&fields=compact&variants=xl,lg`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          imagesByTag[tag.id] = data.images || data;
        }
      }
      setTaggedImages(imagesByTag);
    } catch (error) {
      console.error('Failed to load tagged images:', error);
    } finally {
      setLoadingImages(false);
    }
  }

  async function createTag() {
    if (!newTagName.trim()) return;
    
    setSavingTag('new');
    try {
      const res = await fetch('/api/tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          label: newTagName.trim()
        }),
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      if (res.ok) {
        const newTag = await res.json();
        // Store color locally since backend doesn't support it yet
        newTag.color = newTagColor;
        setTags(prev => [...prev, newTag]);
        setNewTagName('');
        setNewTagColor('#304849');
        setCreating(false);
        showToast('Tag created', `“${newTag.label || newTag.name}” added.`, 'success');
      }
    } catch (error) {
      console.error('Failed to create tag:', error);
    } finally {
      setSavingTag(null);
    }
  }

  async function updateTag(tagId) {
    if (!editName.trim()) return;
    
    setSavingTag(tagId);
    try {
      const res = await fetch(`/api/tags/${tagId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          label: editName.trim()
        })
      });
      
      if (res.ok) {
        const updatedTag = await res.json();
        // Preserve the color locally
        updatedTag.color = editColor;
        setTags(prev => prev.map(tag => 
          tag.id === tagId ? updatedTag : tag
        ));
        setEditingTag(null);
        showToast('Tag updated', 'Changes saved.', 'success');
      }
    } catch (error) {
      console.error('Failed to update tag:', error);
    } finally {
      setSavingTag(null);
    }
  }

  async function deleteTag(tagId) {
    if (!confirm('Are you sure you want to delete this tag? This will remove it from all photos.')) {
      return;
    }
    
    setDeletingTag(tagId);
    try {
      const res = await fetch(`/api/tags/${tagId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        setTags(prev => prev.filter(tag => tag.id !== tagId));
        showToast('Tag deleted', 'Removed from your library.', 'info');
      }
    } catch (error) {
      console.error('Failed to delete tag:', error);
    } finally {
      setDeletingTag(null);
    }
  }

  function startEditing(tag) {
    setEditingTag(tag.id);
    setEditName(tag.label || tag.name);
    setEditColor(tag.color || '#304849');
  }

  function cancelEditing() {
    setEditingTag(null);
    setEditName('');
    setEditColor('');
  }

  const filteredTags = tags
    .filter(tag => (tag.label || tag.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(tag => (browseFilterColor ? (tag.color === browseFilterColor) : true));

  // Lightbox navigation
  const currentImages = selectedTag ? (taggedImages[selectedTag.id] || []) : [];
  const lightboxImage = lightboxIndex !== null ? currentImages[lightboxIndex] : null;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    if (lightboxIndex > 0) setLightboxIndex(lightboxIndex - 1);
  }, [lightboxIndex]);
  const nextImage = useCallback(() => {
    if (lightboxIndex < currentImages.length - 1) setLightboxIndex(lightboxIndex + 1);
  }, [lightboxIndex, currentImages.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, closeLightbox, prevImage, nextImage]);
  const isViewingTag = viewMode === 'view' && selectedTag;
  const viewCount = selectedTag ? (taggedImages[selectedTag.id]?.length || 0) : 0;
  const heroTitle = isViewingTag ? (selectedTag.label || selectedTag.name || 'Tag') : 'Tags';
  const heroSubtitle = isViewingTag
    ? `${viewCount} photo${viewCount === 1 ? '' : 's'} tagged`
    : 'Create and organize tags for your photos.';
  const heroActions = !isViewingTag ? (
    <div className="page-hero-actions">
      {!creating ? (
        <button className="button-primary" onClick={() => setCreating(true)}>
          <FiPlus /> New Tag
        </button>
      ) : (
        <button className="button-secondary" onClick={() => { setCreating(false); setNewTagName(''); setNewTagColor('#304849'); }}>
          <FiX /> Cancel
        </button>
      )}
    </div>
  ) : null;

  if (loading) {
    return (
      <>
        <PageHero
          eyebrow="Organize"
          title="Tags"
        />
        <PageHelp
          title="How to use Tags"
          description="Tags help you group related photos across albums."
          tips={[
            'Create tags for clients, locations, or project themes.',
            'Apply tags from the Photos page using selection mode.',
            'Use tag browsing to jump into a focused set of images.'
          ]}
          subject="Photo Harbor Help - Tags"
        />
        <section className="page-surface page-surface--padded">
          <div className="tags-controls">
            <Skeleton width="200px" height="40px" />
            <Skeleton width="120px" height="40px" />
          </div>
          
          <div className="tags-list">
            {[1, 2, 3].map(i => (
              <div key={i} className="tag-item">
                <Skeleton width="100%" height="60px" />
              </div>
            ))}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Organize"
        title={heroTitle}
        stats={isViewingTag ? [{ label: 'Photos', value: viewCount }] : []}
      >
        {heroActions}
      </PageHero>
      <div className="page-toolbar">
        <PageHelp
          title="How to use Tags"
          description="Tags help you group related photos across albums."
          tips={[
            'Create tags for clients, locations, or project themes.',
            'Apply tags from the Photos page using selection mode.',
            'Use tag browsing to jump into a focused set of images.'
          ]}
          subject="Photo Harbor Help - Tags"
        />
      </div>
      <section className="page-surface page-surface--padded">
        {viewMode === 'view' && selectedTag ? (
          // View mode - showing photos for a specific tag
          <div className="tag-view-mode">
            <div className="tag-view-header">
              <button 
                className="back-button"
                onClick={() => {
                  setViewMode('unified');
                  setSelectedTag(null);
                }}
              >
                ← Back to Tags
              </button>
              <div className="tag-view-title">
                <span 
                  className="tag-color-dot"
                  style={{ backgroundColor: selectedTag.color || '#304849' }}
                />
                <h3>{selectedTag.label || selectedTag.name}</h3>
                <span className="photo-count">
                  {(taggedImages[selectedTag.id] || []).length} photos
                </span>
              </div>
            </div>
            
            <div className="tag-photos-grid">
              {loadingImages ? (
                <div className="loading-photos">
                  <p>Loading photos...</p>
                </div>
              ) : (
                (taggedImages[selectedTag.id] || []).map((image, idx) => (
                  <div
                    key={image.id}
                    className="tag-photo-tile"
                    onClick={() => setLightboxIndex(idx)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={`/media/variants/md/${(image.thumbnail_url || image.filename).replace(/\.[^.]+$/, '.jpg')}`}
                      alt={image.title || 'Photo'}
                      loading="lazy"
                    />
                    {image.title && (
                      <div className="photo-title">{image.title}</div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Lightbox */}
            {lightboxImage && (
              <div className="tags-lightbox-overlay" onClick={closeLightbox}>
                <button className="lightbox-close" onClick={closeLightbox}>
                  <FiX size={24} />
                </button>
                <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                  {lightboxIndex > 0 && (
                    <button className="lightbox-nav lightbox-prev" onClick={prevImage}>
                      <FiChevronLeft size={32} />
                    </button>
                  )}
                  <img
                    src={`/media/variants/xl/${(lightboxImage.thumbnail_url || lightboxImage.filename).replace(/\.[^.]+$/, '.jpg')}`}
                    alt={lightboxImage.title || 'Photo'}
                    className="lightbox-image"
                  />
                  {lightboxIndex < currentImages.length - 1 && (
                    <button className="lightbox-nav lightbox-next" onClick={nextImage}>
                      <FiChevronRight size={32} />
                    </button>
                  )}
                </div>
                <div className="lightbox-counter">
                  {lightboxIndex + 1} / {currentImages.length}
                </div>
              </div>
            )}
          </div>
        ) : (
        <>
        <div className="settings-tabs" role="tablist" aria-label="Tags Mode">
          <button type="button" role="tab" className={`tab-button ${viewMode === 'manage' ? 'is-active' : ''}`} aria-selected={viewMode === 'manage'} onClick={() => setViewMode('manage')}>Manage</button>
          <button type="button" role="tab" className={`tab-button ${viewMode === 'browse' ? 'is-active' : ''}`} aria-selected={viewMode === 'browse'} onClick={() => setViewMode('browse')}>Browse</button>
        </div>
        {viewMode === 'manage' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <div className="segmented-control" role="tablist" aria-label="Manage View">
              <button className={listMode === 'cards' ? 'is-active' : ''} onClick={() => setListMode('cards')}>Cards</button>
              <button className={listMode === 'table' ? 'is-active' : ''} onClick={() => setListMode('table')}>Table</button>
            </div>
          </div>
        )}
        
        <div className="tags-controls">
          <div className="tags-search">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tags-search-input"
            />
          </div>
          {viewMode === 'browse' && (
            <div className="color-presets" aria-label="Filter by color">
              {[null, ...colorPresets.slice(0, 8)].map((c, idx) => (
                <button
                  key={c || 'all'}
                  className={`color-preset ${browseFilterColor === c ? 'selected' : ''}`}
                  style={{ backgroundColor: c || 'var(--bg-secondary)', border: '1px solid var(--border)' }}
                  onClick={() => setBrowseFilterColor(c)}
                  title={c ? `Filter ${c}` : 'All colors'}
                />
              ))}
            </div>
          )}
        </div>
        
        {viewMode === 'manage' && creating && (
          <div className="tag-creator">
            <div className="tag-form">
              <input
                type="text"
                placeholder="Tag name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="tag-name-input"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    createTag();
                  }
                }}
              />
              
              <div className="color-picker">
                <div 
                  className="current-color"
                  style={{ backgroundColor: newTagColor }}
                />
                <div className="color-presets">
                  {colorPresets.map(color => (
                    <button
                      key={color}
                      className={`color-preset ${newTagColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewTagColor(color)}
                    />
                  ))}
                </div>
              </div>
              
              <div className="tag-form-actions">
                <button 
                  className="button-primary"
                  onClick={createTag}
                  disabled={!newTagName.trim() || savingTag === 'new'}
                >
                  <FiCheck /> {savingTag === 'new' ? 'Creating...' : 'Create Tag'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {viewMode === 'manage' && listMode === 'table' ? (
          <div className="ds-table-wrapper">
            <table className="ds-table" role="grid" aria-label="Tags">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Photos</th>
                  <th>Color</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTags.length === 0 ? (
                  <tr><td colSpan="4">No tags</td></tr>
                ) : filteredTags.map(tag => {
                  const images = taggedImages[tag.id] || [];
                  const count = tag.photoCount !== undefined ? tag.photoCount : images.length;
                  return (
                    <tr key={tag.id}>
                      <td><strong>{tag.label || tag.name}</strong></td>
                      <td>{count}</td>
                      <td>
                        <span className="pill" style={{ background: tag.color || '#304849', color: '#fff', borderColor: 'transparent' }}>{tag.color || '#304849'}</span>
                      </td>
                      <td>
                        <div className="tag-actions">
                          <button className="button-icon" onClick={() => startEditing(tag)}><FiEdit2 /></button>
                          <button className="button-icon danger" onClick={() => deleteTag(tag.id)} disabled={deletingTag === tag.id}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
        <div className="tags-unified-grid">
          {filteredTags.length === 0 ? (
            <div className="no-tags">
              {searchQuery ? (
                <>
                  <FiSearch size={48} />
                  <h3>No tags found</h3>
                  <p>Try searching with different keywords</p>
                </>
              ) : (
                <>
                  <FiTag size={48} />
                  <h3>No tags yet</h3>
                  <p>Create your first tag to get started</p>
                </>
              )}
            </div>
          ) : (
            filteredTags.map(tag => {
              const images = taggedImages[tag.id] || [];
              const displayImages = images.slice(0, 4);
              
              return (
              <div key={tag.id} className="tag-unified-card">
                <div 
                  className="tag-card-preview-section"
                  onClick={() => {
                    if (images.length > 0) {
                      setSelectedTag(tag);
                      setViewMode('view');
                    }
                  }}
                  style={{ cursor: images.length > 0 ? 'pointer' : 'default' }}
                >
                  {displayImages.length === 1 ? (
                    <img 
                      src={`/media/variants/md/${(displayImages[0].thumbnail_url || displayImages[0].filename).replace(/\.[^.]+$/, '.jpg')}`}
                      alt=""
                      className="tag-preview-single"
                      loading="lazy"
                    />
                  ) : displayImages.length > 1 ? (
                    <div className="tag-preview-grid">
                      {displayImages.map((image, idx) => (
                        <img 
                          key={image.id}
                          src={`/media/variants/md/${(image.thumbnail_url || image.filename).replace(/\.[^.]+$/, '.jpg')}`}
                          alt=""
                          className="tag-preview-quad"
                          loading="lazy"
                        />
                      ))}
                      {displayImages.length < 4 && Array(4 - displayImages.length).fill(null).map((_, idx) => (
                        <div key={`empty-${idx}`} className="tag-preview-empty" />
                      ))}
                    </div>
                  ) : (
                    <div className="tag-card-placeholder">
                      <FiTag size={48} />
                    </div>
                  )}
                  {images.length > 4 && (
                    <div className="tag-card-overlay">
                      <span>+{images.length - 4}</span>
                    </div>
                  )}
                </div>
                
                <div className="tag-info-section">
                  {viewMode === 'manage' && editingTag === tag.id ? (
                  <div className="tag-edit-form-inline">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="tag-name-input"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          updateTag(tag.id);
                        }
                      }}
                    />
                    
                    <div className="color-picker compact">
                      <div 
                        className="current-color"
                        style={{ backgroundColor: editColor }}
                      />
                      <div className="color-presets">
                        {colorPresets.map(color => (
                          <button
                            key={color}
                            className={`color-preset ${editColor === color ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setEditColor(color)}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div className="tag-actions">
                      <button 
                        className="button-icon"
                        onClick={() => updateTag(tag.id)}
                        disabled={savingTag === tag.id}
                      >
                        <FiCheck />
                      </button>
                      <button 
                        className="button-icon"
                        onClick={cancelEditing}
                      >
                        <FiX />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="tag-info">
                      <div 
                        className="tag-color-indicator"
                        style={{ backgroundColor: tag.color || '#304849' }}
                      />
                      <span className="tag-name">{tag.label || tag.name}</span>
                      {tag.photoCount !== undefined && (
                        <span className="tag-count">
                          {tag.photoCount} {tag.photoCount === 1 ? 'photo' : 'photos'}
                        </span>
                      )}
                    </div>
                    
                    {viewMode === 'manage' && (
                      <div className="tag-actions">
                        <button className="button-icon" onClick={() => startEditing(tag)}>
                          <FiEdit2 />
                        </button>
                        <button className="button-icon danger" onClick={() => deleteTag(tag.id)} disabled={deletingTag === tag.id}>
                          <FiTrash2 />
                        </button>
                      </div>
                    )}
                  </>
                )}
                </div>
                
                <div className="tag-card-info">
                  <span 
                    className="tag-color-dot"
                    style={{ backgroundColor: tag.color || '#304849' }}
                  />
                  <h4>{tag.label || tag.name}</h4>
                  <span className="tag-photo-count">
                    {images.length} {images.length === 1 ? 'photo' : 'photos'}
                  </span>
                </div>
              </div>
              );
            })
          )}
        </div>
        )}
        </>
        )}
      </section>
    </>
  );
}
